/**
 * The resource grid: aircraft and instructors as rows, hours across. Shared by
 * the schedule board and the student portal, where only the student's own
 * flights are drawn.
 *
 * The board is a picture of the day, not a way to edit it. A block is opened,
 * and what happens to the reservation is decided in the drawer that opens —
 * where the school can see what it is agreeing to. Nothing here moves a
 * reservation, so nothing here can move one by accident.
 */
import { useEffect, useMemo, useState } from 'react';
import type { Finding, Reservation } from '../../../shared/types.js';

export interface Block {
  id: number; startHour: number; endHour: number;
  kind: string; status: string; title: string; sub: string; label: string;
  flagged: boolean;
}
export interface Row { id: number; name: string; sub: string; color: string; grounded: boolean; blocks: Block[] }
export interface DayBoard {
  date: string; dayStartHour: number; dayEndHour: number; nowHour: number | null;
  sunrise: number | null; sunset: number | null; civilEnd: number | null;
  rows: { aircraft: Row[]; instructors: Row[] };
  reservations: Reservation[];
  alerts: Finding[];
}

export const LEGEND: [string, string][] = [
  ['Dual', 'dual'], ['Solo', 'solo'], ['Checkride', 'checkride'], ['Rental / block', 'rental'],
  ['Requested', 'pending'], ['Conflict', 'conflict'], ['Maintenance', 'maint'],
];

export function shiftDate(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function longDate(date: string): string {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  });
}

/**
 * Whether this is a device with a cursor.
 *
 * A fingertip needs a taller lane than a mouse pointer does. Reading the
 * pointer rather than the width is what matters: a tablet with a trackpad is
 * a cursor, and a touchscreen laptop is not.
 */
function useFinePointer(): boolean {
  const [fine, setFine] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(pointer: fine)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(pointer: fine)');
    const onChange = () => setFine(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return fine;
}

/* ── The grid ─────────────────────────────────────────────────────── */

export function ResourceGrid({ board, onOpen, ownOnly }: {
  board: DayBoard;
  onOpen: (id: number) => void;
  /** The student portal, where the rows are the school's and the blocks are not. */
  ownOnly?: boolean;
}): JSX.Element {
  const finePointer = useFinePointer();
  const span = board.dayEndHour - board.dayStartHour;
  const hours = useMemo(
    () => Array.from({ length: span }, (_, i) => board.dayStartHour + i),
    [board.dayStartHour, span],
  );
  const pct = (hour: number) => ((hour - board.dayStartHour) / span) * 100;

  const renderRows = (kind: 'aircraft' | 'instructors', label: string, meta: string) => (
    board.rows[kind].length === 0 ? null : (
    <div key={kind}>
      <div className="grid__group"><span>{label}</span><span>{meta}</span></div>
      {board.rows[kind].map((row) => {
        const placed = layout(row.blocks);
        /*
         * Give a row enough height that every stacked booking stays readable —
         * and, on a touch screen, enough that a booking can be hit with a
         * finger. A block fills its lane, so the lane is what has to be 44px:
         * at the 30px a cursor is happy with, opening a lesson on a phone is
         * a matter of aim. It costs a couple of rows of the fleet on screen.
         */
        const lanes = placed[0]?.lanes ?? 1;
        const laneHeight = finePointer ? 30 : 44;
        const rowHeight = lanes === 1 ? (finePointer ? 34 : 48) : lanes * laneHeight;
        return (
        <div className="grid__row" key={row.id}>
          <div className="grid__gutter">
            <span className="grid__strip" style={{ background: row.color }} />
            <span style={{ minWidth: 0 }}>
              <span className="grid__name">{row.name}</span>
              <span className="grid__sub">{row.sub}</span>
            </span>
          </div>
          <div className="grid__lane" style={{ height: rowHeight }}>
            {board.nowHour != null && board.nowHour >= board.dayStartHour && board.nowHour <= board.dayEndHour && (
              <span className="grid__now" style={{ left: `${pct(board.nowHour)}%` }} />
            )}
            {placed.map(({ block, lane, lanes: laneCount }) => {
              return (
                <button
                  key={block.id}
                  className={`block k-${block.kind}`}
                  style={{
                    left: `${pct(block.startHour)}%`,
                    width: `${((block.endHour - block.startHour) / span) * 100}%`,
                    top: `calc(${(lane * 100) / laneCount}% + 2px)`,
                    height: `calc(${100 / laneCount}% - 4px)`,
                  }}
                  title={block.label}
                  onClick={() => onOpen(block.id)}
                >
                  <span className="block__strip" style={{ background: 'currentColor', opacity: 0.55 }} />
                  <span style={{ minWidth: 0, overflow: 'hidden' }}>
                    <span className="block__t">{block.title}</span>
                    <span className="block__s">{block.sub}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        );
      })}
    </div>
    )
  );

  return (
    <div className="card">
      <div className="grid__scroll">
        <div className="grid__head">
          <div className="grid__gutter grid__gutter--head">Resource</div>
          <div className="grid__hours">
            {hours.map((h) => (
              <div className="grid__hour" key={h}>{String(h).padStart(2, '0')}:00</div>
            ))}
          </div>
        </div>
        {renderRows('aircraft', 'Aircraft',
          `${board.rows.aircraft.length} resources · ${board.rows.aircraft.filter((r) => r.grounded).length} grounded`)}
        {renderRows('instructors', 'Instructors', `${board.rows.instructors.length} on duty`)}
      </div>
      <div className="grid__legend">
        {LEGEND.map(([label, kind]) => (
          <span key={kind}>
            <span className={`grid__swatch k-${kind}`} style={{ border: '1px solid', borderLeftWidth: 3 }} />
            {label}
          </span>
        ))}
        <span style={{ marginLeft: 'auto' }}>
          {ownOnly ? 'Only your own flights are shown.' : 'Open a block to see the reservation.'}
        </span>
      </div>
    </div>
  );
}

/** Stack overlapping blocks into sub-lanes so nothing is drawn over anything else. */
export function layout(blocks: Block[]): { block: Block; lane: number; lanes: number }[] {
  const ordered = [...blocks].sort((a, b) => a.startHour - b.startHour);
  const ends: number[] = [];
  const lane = new Map<number, number>();
  for (const b of ordered) {
    let slot = ends.findIndex((end) => end <= b.startHour + 0.001);
    if (slot === -1) { ends.push(b.endHour); slot = ends.length - 1; } else ends[slot] = b.endHour;
    lane.set(b.id, slot);
  }
  const lanes = Math.max(1, ends.length);
  return blocks.map((block) => ({ block, lane: lane.get(block.id) ?? 0, lanes }));
}

export function formatHour(hour: number): string {
  const h = Math.floor(hour);
  const m = Math.round((hour - h) * 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

